'use client'

import { cva } from 'class-variance-authority'
import { useState } from 'react'
import type { WithInputBaseProps } from '@/components/InputBase/InputBase'
import { cn, warnIfEmptyLabel } from '@/utils'
import { Back, Close, Search } from '@/icons'
import { IconButton, InputBase } from '@/components'

const iconVariants = cva(
  'closet-ui-input-search__icon size-3.5 text-gray-600',
  {
    variants: {
      disabled: {
        true: '!text-gray-500',
      },
    },
  },
)

const btnCloseVariants = cva('', {
  variants: {
    disabled: {
      true: ['hidden'],
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

/**
 * InputSearch component props
 */
export type InputSearchProps = WithInputBaseProps<
  React.ComponentPropsWithoutRef<'input'>
> & {
  /** Initial value when the input is left uncontrolled. */
  defaultValue?: string
  /** Current value when the input is controlled. */
  value?: string
  ref?: React.RefObject<HTMLInputElement | null>
  /** Called when the clear button is pressed, after the input is emptied. */
  onClear?: () => void
  /**
   * `type` of the submit button. Use `submit` inside a form, `button` when the
   * search is handled in JavaScript. Defaults to `submit`.
   */
  submitButtonType?: 'submit' | 'button' | 'reset'
  /**
   * Accessible name for the search submit button. Required — provide a
   * localized string from the consuming app.
   */
  submitAriaLabel: string
  /**
   * Accessible name for the clear button shown while the input has a value.
   * Required — provide a localized string from the consuming app.
   */
  clearAriaLabel: string
} & (
    | {
        /**
         * Whether to render the back button that removes focus from the
         * input. When `true`, `backButtonAriaLabel` becomes required —
         * there is no default, so every call site must decide explicitly.
         */
        showBackButton: true
        /**
         * Accessible name for the back button. Required when
         * `showBackButton` is `true` — provide a localized string from the
         * consuming app.
         */
        backButtonAriaLabel: string
      }
    | { showBackButton: false; backButtonAriaLabel?: never }
  )

/**
 * InputSearch can be used to search for items in a context.
 * It is a wrapper around the InputBase component with a search icon and a clear button.
 *
 * Note that it overrides one `InputBase` default: `isRounded` is `true` here,
 * giving a pill frame, where `InputBase` itself defaults to `false`. `size` is
 * passed straight through, so it still defaults to `md`.
 */
export const InputSearch = ({
  ref,
  className,
  startAdornment,
  endAdornment,
  size,
  style,
  isRounded = true,
  variant,
  disabled,
  helperText,
  state,
  value,
  defaultValue,
  onChange,
  onClear,
  submitButtonType = 'submit',
  showBackButton,
  backButtonAriaLabel,
  submitAriaLabel,
  clearAriaLabel,
  ...props
}: InputSearchProps) => {
  warnIfEmptyLabel(submitAriaLabel, 'InputSearch: submitAriaLabel')
  warnIfEmptyLabel(clearAriaLabel, 'InputSearch: clearAriaLabel')
  if (showBackButton) {
    warnIfEmptyLabel(backButtonAriaLabel, 'InputSearch: backButtonAriaLabel')
  }

  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? '')

  const inputValue = isControlled ? value : internalValue
  const hasValue = inputValue.length > 0

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) {
      setInternalValue(e.target.value)
    }

    onChange?.(e)
  }

  const handleClearAndSetFocus = () => {
    if (!isControlled) {
      setInternalValue('')
    } else {
      onClear?.()
    }

    ref?.current?.focus()
  }

  const handleBack = () => {
    ref?.current?.blur()
  }

  return (
    <div
      className={cn(
        'closet-ui-input-search group flex items-center gap-2',
        className,
      )}
    >
      {showBackButton && (
        <IconButton
          data-testid="btn-back"
          size="sm"
          variant="subtle"
          type="button"
          className="closet-ui-input-search__btn-back hidden shrink-0 group-focus-within:flex lg:group-focus-within:hidden"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleBack}
          aria-label={backButtonAriaLabel}
        >
          <Back />
        </IconButton>
      )}

      <InputBase
        className="min-w-0 flex-1"
        size={size}
        style={style}
        isRounded={isRounded}
        variant={variant}
        disabled={disabled}
        helperText={helperText}
        state={state}
        inputContainerClassName="focus-within:bg-white"
        startAdornment={
          startAdornment || (
            <Search className={cn(iconVariants({ disabled }), 'lg:hidden')} />
          )
        }
        endAdornment={
          endAdornment || (
            <div className="flex items-center">
              {hasValue && !disabled && (
                <IconButton
                  data-testid="btn-clear"
                  size="sm"
                  variant="subtle"
                  className={cn(
                    'closet-ui-input-search__btn-clea',
                    btnCloseVariants({ disabled }),
                  )}
                  onClick={handleClearAndSetFocus}
                  type="button"
                  aria-label={clearAriaLabel}
                >
                  <Close className={iconVariants({ disabled })} />
                </IconButton>
              )}
              <div className="hidden items-center lg:flex">
                <div className="mx-2 h-6 w-px bg-gray-300" aria-hidden="true" />
                <IconButton
                  data-testid="btn-search"
                  size="sm"
                  disabled={disabled || undefined}
                  type={submitButtonType}
                  variant="subtle"
                  aria-label={submitAriaLabel}
                >
                  <Search className={iconVariants({ disabled })} />
                </IconButton>
              </div>
            </div>
          )
        }
      >
        <input
          spellCheck={false}
          autoComplete="off"
          type="text"
          enterKeyHint="search"
          data-testid="input"
          value={inputValue}
          className="lg:pr-23 h-full w-full px-12 outline-none lg:pl-4"
          ref={ref}
          disabled={disabled ?? false}
          onChange={handleChange}
          {...props}
        />
      </InputBase>
    </div>
  )
}
